import { Filter, Config } from "../../context";
import { getColumnValue } from "../../util";
import { ColumnType, DateConfig } from "../../columnConfig";
import { isSameDay, isAfter, isBefore } from "date-fns";

type Filterer = (
  filterValue: any,
  resolvedValue: any,
  originalValue: any,
  config: any
) => boolean;

type Tag = {
  id: string;
  name: string;
};

const filtersByType: Record<ColumnType, Filterer> = {
  string: (filterValue: string, resolvedValue: string) => {
    return resolvedValue.toLowerCase().includes(filterValue.toLowerCase());
  },
  bool: (filterValue: boolean, _: any, originalValue: any) => {
    return Boolean(originalValue) === filterValue;
  },
  date: (
    filterValue: any,
    resolvedValue: string | Date | null,
    _,
    config: DateConfig
  ) => {
    if (config.selectType === "range") {
      const { start, end } = filterValue;

      if (!start || !end) {
        return true;
      }

      if (!resolvedValue) {
        return false;
      }

      const isAfterOrEqualToStart =
        isSameDay(start, resolvedValue) || isAfter(resolvedValue, start);
      const isBeforeOrEqualToStart =
        isSameDay(end, resolvedValue) || isBefore(resolvedValue, end);

      return isAfterOrEqualToStart && isBeforeOrEqualToStart;
    }

    if (!resolvedValue) {
      return false;
    }

    return isSameDay(filterValue, resolvedValue);
  },
  range: (
    filterValue: { min: number; max: number },
    resolvedValue: number | null
  ) => {
    return (
      (resolvedValue || 0) >= filterValue.min &&
      (resolvedValue || 0) <= filterValue.max
    );
  },
  enum: (filterValue: any, resolvedValue: any) => {
    return filterValue === resolvedValue;
  },
  tags: (filterValue: Tag[], resolvedValue: Tag[] | null) => {
    if (!filterValue || filterValue.length === 0) {
      return true;
    }

    if (!resolvedValue || resolvedValue.length === 0) {
      return false;
    }

    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < resolvedValue.length; i++) {
      const columnTag = resolvedValue[i];

      const foundTag = filterValue.find(
        filterTag => filterTag.id === columnTag.id
      );
      if (foundTag) {
        return true;
      }
    }

    return false;
  }
};

function filterData(
  data: any[],
  filters: Record<string, Filter>,
  configs: Config[],
  t: any
) {
  const keys = Object.keys(filters);

  return keys.reduce((data, key) => {
    const filter = filters[key];

    // just pass data through when filter is not active
    if (filter.value === null || filter.value === undefined) {
      return data;
    }

    // get column-config
    const config = configs.find(config => config.key === key)!;

    // get right filterer for column type
    const filterer = filtersByType[config.type];

    return data.filter(row => {
      const value = (getColumnValue(row, config, t) as string) || "";
      return filterer(filter.value, value, row[key], config);
    });
  }, data || []);
}

export default filterData;
